/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { usePropertyCorrections } from '@/contexts/PropertyCorrectionsContext';
import MultiImageUploader from './MultiImageUploader';

interface CountyVerificationProps {
  county: string;
  state: string;
  onVerified: (
    dateRange?: { startDate: Date; endDate: Date },
    comparableData?: any[],
  ) => void;
}

// Component state for tracking button clicks
interface ComponentState {
  verifyButtonClicked: boolean;
  continueButtonClicked: boolean;
}

// Get the appropriate API base URL based on environment
const getApiBaseUrl = () => {
  return import.meta.env.DEV
    ? 'http://localhost:8080'
    : 'https://property-tax-api.yourwayhome.com';
};

// Function to fetch county procedures from the API
const fetchCountyProcedures = async (county: string, state: string) => {
  try {
    const response = await fetch(
      `${getApiBaseUrl()}/api/v1/properties/summary`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          county: county,
          state: state,
        }),
      },
    );

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    const data = await response.json();

    window.localStorage.setItem(
      'assessorInfo',
      JSON.stringify({
        address: data.data.AssessorContact.Address,
        name: data.data.AssessorContact.Name,
        phone: data.data.AssessorContact.Phone,
        websiteUrl: data.data.AssessorContact.Website,
        protestUrl: data.data.AssessorContact.ProtestInfoLink,
      }),
    );

    return data;
  } catch (error) {
    console.error('Error fetching county procedures:', error);
    throw error;
  }
};

// Fallback county procedures data
const fallbackCountyProcedures = {
  title: 'Boulder County Property Tax Appeal Procedures',
  content: `
    <div class="space-y-4">
      <h3 class="text-lg font-semibold">Boulder County Assessor's Office</h3>
      <p><strong>Website:</strong> <a href="https://www.bouldercounty.org/property/assessor/" class="text-blue-600 underline" target="_blank">www.bouldercounty.org/property/assessor/</a></p>
      
      <h4 class="font-semibold">Key Deadlines:</h4>
      <ul class="list-disc list-inside space-y-1">
        <li>Notice of Valuation mailed: May 1st</li>
        <li>Protest deadline: June 8th (30 days after notice)</li>
        <li>County Board of Equalization hearings: July-August</li>
        <li>State Board of Assessment Appeals deadline: August 30th</li>
      </ul>
      
      <h4 class="font-semibold">Appeal Process:</h4>
      <ol class="list-decimal list-inside space-y-1">
        <li>File protest with County Assessor within 30 days of Notice of Valuation</li>
        <li>Provide evidence supporting your claimed value</li>
        <li>Attend informal conference with assessor staff</li>
        <li>If not resolved, appeal to County Board of Equalization</li>
        <li>Final appeal to State Board of Assessment Appeals if needed</li>
      </ol>
      
      <h4 class="font-semibold">Required Documentation:</h4>
      <ul class="list-disc list-inside space-y-1">
        <li>Completed protest form</li>
        <li>Comparable property sales data</li>
        <li>Property condition documentation</li>
        <li>Any relevant appraisals or inspections</li>
      </ul>
      
      <p class="text-sm text-gray-600 mt-4">
        <strong>Contact:</strong> Boulder County Assessor's Office<br/>
        Phone: (303) 441-3530<br/>
        Email: assessor@bouldercounty.org
      </p>
    </div>
  `,
};

// Generate procedures content from API data
const generateProceduresContent = (apiData: any) => {
  const data = apiData.data;
  const assessor = data.AssessorContact;
  const protest = data.ProtestAppeal;
  const assessment = data.Assessment;

  return `
    <div class="space-y-4">
      <h3 class="text-lg font-semibold">${assessor.Name}</h3>
      <p><strong>Website:</strong> <a href="${
        assessor.Website
      }" class="text-blue-600 underline" target="_blank">${
    assessor.Website
  }</a></p>
      <p><strong>Address:</strong> ${assessor.Address}</p>
      <p><strong>Phone:</strong> ${assessor.Phone}</p>
      
      <h4 class="font-semibold">Key Deadlines (per state; verify with local assessor):</h4>
      <ul class="list-disc list-inside space-y-1">
        <li>Notice of Valuation mailed: ${
          assessment.NoticeOfValueMailedDate
        }</li>
        <li>Protest deadline: ${protest.Deadline}</li>
        <li>Valuation as of date: ${protest.ValuationAsOfDate}</li>
      </ul>
      
      <h4 class="font-semibold">Appeal Process:</h4>
      <p>${protest.ProcessDescription}</p>
      
      <h4 class="font-semibold">Summary of process per state guidelines (verify with your local assessor):</h4>
      <p>${data.SummaryOfProcess.Process}</p>
      
      <h4 class="font-semibold">Comparable Sales Requirements:</h4>
      <ul class="list-disc list-inside space-y-1">
        <li>${
          protest.ComparableSalesMinimumDate
            ? `Sales must be between ${protest.ComparableSalesMinimumDate} and ${protest.ComparableSalesMaximumDate}`
            : 'Not specified'
        }</li>
        <li>Provide evidence supporting your claimed value</li>
        <li>Property condition documentation</li>
        <li>Any relevant appraisals or inspections</li>
      </ul>
      
      <h4 class="font-semibold">Property Tax Information:</h4>
      <ul class="list-disc list-inside space-y-1">
        <li>Number of installments: ${data.PropertyTax.Installments}</li>
        <li>Due dates: ${data.PropertyTax.DueDates}</li>
        <li>Payment timing: ${data.PropertyTax.PaidInAdvanceOrArrears}</li>
      </ul>
      
      <h4 class="font-semibold">State Property Tax Guidelines:</h4>
      <p><strong>Website:</strong> <a href="${
        data.StatePropertyTaxGuidelines.Website
      }" class="text-blue-600 underline" target="_blank">${
    data.StatePropertyTaxGuidelines.Website
  }</a></p>
      
      <p class="text-sm text-gray-600 mt-4">
        <strong>Additional Resources:</strong><br/>
        <a href="${
          assessor.ProtestInfoLink
        }" class="text-blue-600 underline" target="_blank">Property Valuation Appeal Information</a>
      </p>
      
      <p class="text-xs text-gray-500 mt-4">
        ${data.Verification}
      </p>
    </div>
  `;
};

const CountyVerification: React.FC<CountyVerificationProps> = ({
  county,
  state,
  onVerified,
}) => {
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationComplete, setVerificationComplete] = useState(false);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [startDate, setStartDate] = useState<Date>(new Date('2022-05-01'));
  const [endDate, setEndDate] = useState<Date>(new Date('2024-06-01'));
  const [showProcedures, setShowProcedures] = useState(false);
  const [savedProcedures, setSavedProcedures] = useState<string>('');
  const [apiData, setApiData] = useState<any>(null);
  const [isLoadingComparables, setIsLoadingComparables] = useState(false);
  const [verifyButtonClicked, setVerifyButtonClicked] = useState(false);
  const [continueButtonClicked, setContinueButtonClicked] = useState(false);

  const { corrections } = usePropertyCorrections();

  const verificationMessages = [
    'Reviewing County Assessor Website',
    'Checking Appeal Procedures',
    'Verifying Deadlines',
  ];

  const comparableMessages = [
    '🧭 Locating nearby property sales...',
    '📏 Matching homes by size, age, and location...',
    '🏘️ Checking sales within your neighborhood...',
    '🕰️ Reviewing recent market activity...',
    '🧠 Filtering for the most relevant comps...',
    '📬 Cross-checking public records...',
    '📊 Calculating value adjustments...',
    '🔍 Scanning wider area for similar homes...',
    '📦 Wrapping up your comparison report...',
    '✅ Comps found! Generating results...',
  ];

  // Function to fetch comparable properties
  const fetchComparableProperties = async (
    criteria: any,
    saleDateMin: string,
    saleDateMax: string,
  ) => {
    try {
      console.log({ criteria });

      const payload = {
        buildingSquareFeet:
          corrections.livingAreaSqft || criteria.livingSquareFeet,
        yearBuilt: criteria.yearBuilt,
        propertyUseCode: criteria.propertyUseCode,
        propertyUse: criteria.propertyUse,
        longitude: criteria.longitude,
        latitude: criteria.latitude,
        county: criteria.county || '',
        state: criteria.state || '',
        saleDateMin: saleDateMin,
        saleDateMax: saleDateMax,
      };

      console.log('Fetching comparables with payload:', payload);

      const response = await fetch(
        `${getApiBaseUrl()}/api/v1/properties/compare`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        throw new Error('Failed to fetch comparable properties');
      }

      const data = await response.json();
      console.log('Comparable properties response:', data);
      window.localStorage.setItem(
        'comparableProperties',
        JSON.stringify(data.data),
      );

      if (data && data.data && Array.isArray(data.data)) {
        return data.data;
      }

      return [];
    } catch (error) {
      console.error('Error fetching comparable properties:', error);
      throw error;
    }
  };

  const handleVerifyCounty = async () => {
    setVerifyButtonClicked(true);
    setIsVerifying(true);
    setCurrentMessageIndex(0);

    // Extract state from county string (assuming format like "Boulder County, CO")
    const countyParts = county.split(', ');
    const countyName = countyParts[0];
    const stateName = state; // Default to CO if not specified

    console.log('Fetching county procedures for:', countyName, stateName);

    // Cycle through messages every 1 second
    const messageInterval = setInterval(() => {
      setCurrentMessageIndex((prevIndex) => {
        if (prevIndex < verificationMessages.length - 1) {
          return prevIndex + 1;
        }
        return prevIndex;
      });
    }, 1000);

    try {
      // Make API call to fetch county procedures
      const data = await fetchCountyProcedures(countyName, stateName);
      setApiData(data);

      let asOfDate: Date | null = null;

      // Example: data.data.ProtestAppeal.ValueAsOfDate (you can adjust key accordingly)
      const valueAsOf = data?.data?.ProtestAppeal?.ValueAsOfDate;

      if (valueAsOf && valueAsOf !== 'Not Specified') {
        asOfDate = new Date(valueAsOf);
      }

      // If no valueAsOf, default to Jan 1 of current year
      if (!asOfDate) {
        asOfDate = new Date(new Date().getFullYear(), 0, 1); // Jan 1
      }

      const minDateStr = data?.data?.ProtestAppeal?.ComparableSalesMinimumDate;
      const maxDateStr = data?.data?.ProtestAppeal?.ComparableSalesMaximumDate;

      const minDateValid = minDateStr && minDateStr !== 'Not Specified';
      const maxDateValid = maxDateStr && maxDateStr !== 'Not Specified';

      if (minDateValid && maxDateValid) {
        setStartDate(new Date(minDateStr));
        setEndDate(new Date(maxDateStr));
      } else {
        const oneYearBefore = new Date(asOfDate);
        oneYearBefore.setFullYear(oneYearBefore.getFullYear() - 1);

        setStartDate(oneYearBefore);
        const _endDate = new Date(asOfDate);
        _endDate.setDate(_endDate.getDate() - 1);
        setEndDate(_endDate);
      }

      // Update date range from API data if available
      // if (data?.data?.ProtestAppeal?.ComparableSalesMinimumDate) {
      //   setStartDate(
      //     new Date(data.data.ProtestAppeal.ComparableSalesMinimumDate),
      //   );
      // }
      // if (data?.data?.ProtestAppeal?.ComparableSalesMaximumDate) {
      //   setEndDate(
      //     new Date(data.data.ProtestAppeal.ComparableSalesMaximumDate),
      //   );
      // }
    } catch (error) {
      console.error('Failed to fetch county procedures:', error);
      // Continue with fallback data
    } finally {
      clearInterval(messageInterval);
      setIsVerifying(false);
      setVerificationComplete(true);
    }
  };

  const handleProceduresClick = () => {
    if (!savedProcedures) {
      // Use API data if available, otherwise use fallback data
      const proceduresContent = apiData
        ? generateProceduresContent(apiData)
        : fallbackCountyProcedures.content;
      setSavedProcedures(proceduresContent);
    }
    setShowProcedures(true);
  };

  const handleContinue = async () => {
    setContinueButtonClicked(true);
    setIsLoadingComparables(true);
    setCurrentMessageIndex(0);

    // Set up message rotation for comparable search
    const messageInterval = setInterval(() => {
      setCurrentMessageIndex((prevIndex) => {
        if (prevIndex < comparableMessages.length - 1) {
          return prevIndex + 1;
        }
        return prevIndex;
      });
    }, 700);

    try {
      // Get comparable search criteria from localStorage or use defaults
      const storedCriteria = window.localStorage.getItem(
        'comparableSearchCriteria',
      );
      let criteria = null;

      if (storedCriteria) {
        criteria = JSON.parse(storedCriteria);
      }

      if (criteria) {
        // Format dates for API call
        const saleDateMin = startDate.toISOString().split('T')[0];
        const saleDateMax = endDate.toISOString().split('T')[0];

        // Call the compare API
        const comparableData = await fetchComparableProperties(
          criteria,
          saleDateMin,
          saleDateMax,
        );

        if (comparableData) {
          onVerified({ startDate, endDate }, comparableData);
        }
      } else {
        setTimeout(() => {
          clearInterval(messageInterval);
          setIsLoadingComparables(false);
          onVerified({ startDate, endDate });
        }, 1000);
      }
    } catch (error) {
      onVerified({ startDate, endDate });
    } finally {
      clearInterval(messageInterval);
      setIsLoadingComparables(false);
    }
  };

  const handleStartDateChange = (date: Date | undefined) => {
    if (date) {
      setStartDate(date);
    }
  };

  const handleEndDateChange = (date: Date | undefined) => {
    if (date) {
      setEndDate(date);
    }
  };

  // Get display values from API data or fallbacks
  const getDisplayData = () => {
    if (apiData?.data) {
      const data = apiData.data;
      return {
        noticeDate: data.Assessment?.NoticeOfValueMailedDate || 'May 1st',
        valuationDate: data.ProtestAppeal?.ValuationAsOfDate || 'June 30, 2024',
      };
    }
    return {
      noticeDate: 'May 1st',
      valuationDate: 'June 30, 2024',
    };
  };

  const displayData = getDisplayData();

  return (
    <div className="mt-6">
      {!isVerifying && !verificationComplete && (
        <div className="relative">
          <Button
            onClick={handleVerifyCounty}
            className="w-full md:max-w-md md:mx-auto bg-primary hover:bg-primary/90 text-primary-foreground lg:text-lg py-6 flex items-center justify-center gap-2 shadow-lg shadow-primary/50 border-2 border-primary/20 truncate px-3"
          >
            <div className="shrink-0 flex items-center justify-center bg-primary text-primary-foreground rounded-full w-7 h-7 mr-2 font-medium">
              2
            </div>
            Verify {county} Appeal Procedures
          </Button>
          {/* Animated Arrow - only show if not clicked */}
          {!verifyButtonClicked && (
            <div className="hidden lg:block absolute right-[-60px] top-1/2 transform -translate-y-1/2 pointer-events-none">
              <div className="animate-pulse">
                <img
                  src="/lovable-uploads/94167cf8-9d18-4d74-9994-fcf247a6c379.png"
                  alt="Click arrow"
                  className="w-10 h-10"
                />
              </div>
            </div>
          )}
        </div>
      )}

      {isVerifying && (
        <div className="bg-white border border-tax-borderGray rounded-lg p-6 shadow-sm">
          <div className="flex flex-col items-center justify-center">
            <div className="animate-pulse mb-4">
              <div className="text-4xl mb-2">⚙️</div>
            </div>
            <h3 className="text-xl font-medium text-tax-deepBlue mb-2">
              Verifying County Procedures
            </h3>
            <p className="text-tax-neutralGray mb-6">
              {verificationMessages[currentMessageIndex]}
            </p>
            <div className="w-full max-w-md">
              <div className="h-2 bg-tax-brightBlue rounded-full">
                <div
                  className="h-2 bg-tax-actionOrange rounded-full animate-pulse"
                  style={{
                    width: `${
                      ((currentMessageIndex + 1) /
                        verificationMessages.length) *
                      100
                    }%`,
                  }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {verificationComplete && (
        <div className="space-y-4">
          {/* Key Dates & Deadlines Card - Always visible */}
          <div className="bg-white border border-tax-borderGray rounded-lg py-6 px-4 lg:px-6 shadow-sm">
            <h3 className="text-xl font-medium text-tax-deepBlue">
              {county} Property Tax Key Dates & Deadlines
            </h3>

            <div className="space-y-2 mt-4">
              <p>
                <strong>Notice of Value Issued:</strong>{' '}
                {displayData.noticeDate}
              </p>
              <p>
                <strong>Valuation for 2025 Tax Year:</strong> As Of{' '}
                {displayData.valuationDate}
              </p>
              <div className="flex items-center gap-2 flex-wrap justify-center">
                <p>
                  <strong>Comparable Properties Must Have Sold Between:</strong>
                </p>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="bg-yellow-100 border-yellow-400 text-yellow-800 hover:bg-yellow-200"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(startDate, 'MMM d, yyyy')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={handleStartDateChange}
                      initialFocus
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
                <span>and</span>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="bg-yellow-100 border-yellow-400 text-yellow-800 hover:bg-yellow-200"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(endDate, 'MMM d, yyyy')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={endDate}
                      onSelect={handleEndDateChange}
                      initialFocus
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <p className="text-sm text-gray-600 mt-4">
              You can view the full procedures for {county}{' '}
              <button
                onClick={handleProceduresClick}
                className="text-tax-brightBlue underline hover:text-tax-brightBlue/80"
              >
                here
              </button>
              .
            </p>
          </div>

          {/* Loading State for Comparable Search */}
          {isLoadingComparables && (
            <div className="bg-white border border-tax-borderGray rounded-lg p-6 shadow-sm">
              <div className="flex flex-col items-center justify-center">
                <div className="animate-pulse mb-4">
                  <div className="text-4xl mb-2">
                    {comparableMessages[currentMessageIndex].split(' ')[0]}
                  </div>
                </div>
                <h3 className="text-xl font-medium text-tax-deepBlue mb-2">
                  Finding Comparable Sales
                </h3>
                <p className="text-tax-neutralGray mb-6">
                  {comparableMessages[currentMessageIndex]}
                </p>
                <div className="w-full max-w-md">
                  <div className="h-2 bg-tax-brightBlue rounded-full">
                    <div
                      className="h-2 bg-tax-actionOrange rounded-full animate-pulse"
                      style={{
                        width: `${
                          ((currentMessageIndex + 1) /
                            comparableMessages.length) *
                          100
                        }%`,
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Continue Button - Only show if not loading */}
          {!isLoadingComparables && (
            <div className="relative">
              <Button
                onClick={handleContinue}
                className="w-full md:max-w-md md:mx-auto bg-primary hover:bg-primary/90 text-primary-foreground text-lg flex items-center justify-center gap-2 shadow-lg shadow-primary/50 border-2 border-primary/20"
              >
                <div className="flex items-center justify-center bg-primary text-primary-foreground rounded-full w-7 h-7 mr-2 font-medium">
                  3
                </div>
                Continue to Find Comparable Sales
              </Button>
              {/* <MultiImageUploader /> */}
              {/* Animated Arrow - only show if not clicked */}
              {!continueButtonClicked && (
                <div className="hidden lg:block absolute right-[-60px] top-1/2 transform -translate-y-1/2 pointer-events-none">
                  <div className="animate-pulse">
                    <img
                      src="/lovable-uploads/94167cf8-9d18-4d74-9994-fcf247a6c379.png"
                      alt="Click arrow"
                      className="w-10 h-10"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Procedures Dialog */}
      <Dialog open={showProcedures} onOpenChange={setShowProcedures}>
        <DialogContent className="max-w-full lg:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {apiData?.data
                ? `${apiData.data.County} Property Tax Appeal Procedures`
                : fallbackCountyProcedures.title}
            </DialogTitle>
            <DialogDescription>
              Complete procedures and deadlines for property tax appeals in{' '}
              {county}.
            </DialogDescription>
          </DialogHeader>
          <div
            className="prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: savedProcedures }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CountyVerification;
